import { Drawer, DrawerHeader, DrawerContent, DrawerFooter, InputLabel, Stack } from '@components/ui';
import {
  Button,
  CircularProgress,
  DrawerProps as MuiDrawerProps,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  OutlinedInput,
  Switch,
  Typography,
} from '@material-ui/core';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NotificationChannel } from 'src/modules/generated/api';

import type { Merge } from 'type-fest';

type SearchAgentDrawerProps = {
  title?: string;
  onClose: () => void;
  onSubmit: (data: SearchAgentFormData) => void;
  onSubmitAndCreatePurchaseAgent?: (data: SearchAgentFormData) => Promise<void>;
  searchAgent: {
    id: string;
    searchAgentDisplayName: string;
    active?: boolean;
    favorite?: boolean;
  };
};

enum DisplayChannel {
  favorites = 'favorites',
}

export type SearchAgentFormData = {
  searchAgentDisplayName: string;
  notifications: Record<NotificationChannel, boolean>;
  display: Record<DisplayChannel, boolean>;
};

export const SearchAgentDrawer = ({
  onClose,
  onSubmit,
  onSubmitAndCreatePurchaseAgent,
  searchAgent: originalSearchAgent,
  ...drawerProps
}: Merge<MuiDrawerProps, SearchAgentDrawerProps>) => {
  const { t } = useTranslation();
  const [isCreatingPA, setIsCreatingPA] = useState(false);
  const originalValues = useMemo(
    () => ({
      searchAgentDisplayName: originalSearchAgent.searchAgentDisplayName,
      notifications: {
        [NotificationChannel.EMail]: originalSearchAgent.active,
      },
      display: {
        [DisplayChannel.favorites]: originalSearchAgent.favorite,
      },
    }),
    [originalSearchAgent.active, originalSearchAgent.favorite, originalSearchAgent.searchAgentDisplayName],
  );
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SearchAgentFormData>({
    defaultValues: originalValues,
  });

  const handleClose = () => {
    onClose();
    reset(originalValues);
  };

  useEffect(() => {
    reset(originalValues);
  }, [originalValues, reset]);

  return (
    <Drawer anchor="right" onClose={handleClose} {...drawerProps}>
      <DrawerHeader>
        <Typography>{t('searchAgentTableDrawer.title')}</Typography>
      </DrawerHeader>
      <DrawerContent>
        <form id="search-agent-edit-form" noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3}>
            <FormControl size="small" fullWidth variant="outlined">
              <InputLabel required>{t('common.name')}</InputLabel>
              <Controller
                name="searchAgentDisplayName"
                control={control}
                rules={{ required: true }}
                render={({ field: { ref, ...field } }) => <OutlinedInput inputRef={ref} {...field} />}
              />
              {errors.searchAgentDisplayName ? (
                <FormHelperText error>{errors.searchAgentDisplayName.type}</FormHelperText>
              ) : null}
            </FormControl>

            <div>
              <InputLabel>{t('common.display')}</InputLabel>
              <FormControlLabel
                control={
                  <Controller
                    control={control}
                    name={`display.${DisplayChannel.favorites}`}
                    render={({ field: { ref, value, ...field } }) => (
                      <Switch inputRef={ref} {...field} checked={value} size="small" color="primary" />
                    )}
                  />
                }
                label={t('searchAgentTableDrawer.display.favorites')}
              />
              {errors.display?.[DisplayChannel.favorites] ? (
                <FormHelperText error>{errors.display?.[DisplayChannel.favorites]?.type}</FormHelperText>
              ) : null}
            </div>

            <div>
              <InputLabel>{t('common.notification_plural')}</InputLabel>
              <FormControlLabel
                control={
                  <Controller
                    control={control}
                    name={`notifications.${NotificationChannel.EMail}`}
                    render={({ field: { ref, value, ...field } }) => (
                      <Switch inputRef={ref} {...field} checked={value} size="small" color="primary" />
                    )}
                  />
                }
                label={t('notification.E_MAIL')}
              />
              {errors.notifications?.[NotificationChannel.EMail] ? (
                <FormHelperText error>{errors.notifications[NotificationChannel.EMail]?.type}</FormHelperText>
              ) : null}
            </div>
          </Stack>
        </form>
      </DrawerContent>
      <DrawerFooter>
        <Grid container justifyContent="space-between">
          <Button size="small" variant="outlined" color="secondary" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Grid item style={{ display: 'flex', gap: 8 }}>
            {onSubmitAndCreatePurchaseAgent && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                disabled={isCreatingPA}
                onClick={handleSubmit(async (data) => {
                  setIsCreatingPA(true);
                  try {
                    await onSubmitAndCreatePurchaseAgent(data);
                  } catch {
                    // error surfaced by mutation hook
                  } finally {
                    setIsCreatingPA(false);
                  }
                })}
              >
                {isCreatingPA ? <CircularProgress size={16} /> : t('searchAgentTableDrawer.saveAndCreatePurchaseAgent')}
              </Button>
            )}
            <Button type="submit" form="search-agent-edit-form" size="small" variant="contained" color="primary">
              {t('common.save')}
            </Button>
          </Grid>
        </Grid>
      </DrawerFooter>
    </Drawer>
  );
};
